// @ts-nocheck
import {
  Box,
  Card as MantineCard,
  SimpleGrid,
  Stack,
  Text as MantineText,
  ThemeIcon,
  Title,
  Badge,
  Group,
} from '@mantine/core';
import {
  IconShoppingCart,
  IconFolders,
  IconUsers,
  IconUserCircle,
  IconFolder,
  IconChartBar,
  IconMessage,
  IconCalendar,
  IconReceipt,
  IconHeadset,
  IconApps,
} from '@tabler/icons-react';
// @ts-nocheck
import { useNavigate } from 'react-router-dom';
import { Card, Text } from '../../../components/ui';

const apps = [
  {
    icon: IconShoppingCart,
    title: 'E-Commerce',
    description: 'Complete online store admin with sales analytics, product catalog, orders, and customer management',
    path: '/app/demo/apps/ecommerce',
    color: 'blue',
    pages: ['Dashboard', 'Products', 'Orders', 'Customers'],
  },
  {
    icon: IconFolders,
    title: 'Project Management',
    description: 'Project tracking with Kanban boards, timelines, team collaboration, and progress monitoring',
    path: '/app/demo/apps/project-management',
    color: 'violet',
    pages: ['Dashboard', 'Projects', 'Board', 'Team'],
  },
  {
    icon: IconUsers,
    title: 'CRM',
    description: 'Customer relationship management with deal pipeline, contacts, and activity tracking',
    path: '/app/demo/apps/crm',
    color: 'green',
    pages: ['Dashboard', 'Pipeline', 'Contacts', 'Activities'],
  },
  {
    icon: IconUserCircle,
    title: 'HR Portal',
    description: 'Human resources portal with employee directory, org chart, and leave management',
    path: '/app/demo/apps/hr-portal',
    color: 'orange',
    pages: ['Dashboard', 'Directory', 'Organization', 'Leave'],
  },
  {
    icon: IconFolder,
    title: 'File Manager',
    description: 'Cloud storage interface with file browser, uploads, sharing, and document previews',
    path: '/app/demo/apps/file-manager',
    color: 'cyan',
    pages: ['Files', 'Recent', 'Shared', 'Trash'],
  },
  {
    icon: IconChartBar,
    title: 'Analytics',
    description: 'Data visualization dashboard with KPIs, charts, report builder, and real-time metrics',
    path: '/app/demo/apps/analytics',
    color: 'pink',
    pages: ['Overview', 'Reports', 'Realtime', 'Exports'],
  },
  {
    icon: IconMessage,
    title: 'Chat',
    description: 'Messaging application with conversations, contacts, group chats, and notifications',
    path: '/app/demo/apps/chat',
    color: 'indigo',
    pages: ['Messages', 'Contacts', 'Groups', 'Settings'],
  },
  {
    icon: IconCalendar,
    title: 'Calendar',
    description: 'Scheduling app with calendar views, meeting scheduler, and availability management',
    path: '/app/demo/apps/calendar',
    color: 'teal',
    pages: ['Calendar', 'Schedule', 'Availability', 'Events'],
  },
  {
    icon: IconReceipt,
    title: 'Invoicing',
    description: 'Billing system with invoice creation, payment tracking, and financial reports',
    path: '/app/demo/apps/invoicing',
    color: 'yellow',
    pages: ['Dashboard', 'Invoices', 'Create', 'Payments'],
  },
  {
    icon: IconHeadset,
    title: 'Helpdesk',
    description: 'Support ticketing system with Kanban board, ticket management, and knowledge base',
    path: '/app/demo/apps/helpdesk',
    color: 'red',
    pages: ['Dashboard', 'Tickets', 'Board', 'Knowledge'],
  },
];

export default function AppsOverviewPage() {
  const navigate = useNavigate();

  return (
    <Stack gap="xl">
      {/* Header */}
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Demo Applications
        </Text>
        <MantineText c="dimmed" size="sm">
          Explore complete application templates built with our component library - similar to ThemeForest templates
        </MantineText>
      </Box>

      {/* Welcome Card */}
      <Card variant="gradient" gradientVariant="secondary" hoverEffect="glow">
        <Stack gap="md">
          <Group gap="lg">
            <ThemeIcon size={60} radius="xl" variant="white" color="dark">
              <IconApps size={32} />
            </ThemeIcon>
            <Box style={{ flex: 1 }}>
              <Title order={3} c="white">
                10 Complete Application Templates
              </Title>
              <MantineText c="white" opacity={0.9}>
                Each application includes multiple pages with realistic mock data, interactive components,
                and production-ready layouts. Perfect for understanding how to build real-world applications.
              </MantineText>
            </Box>
          </Group>
        </Stack>
      </Card>

      {/* Apps Grid */}
      <Box>
        <Title order={3} mb="md">
          Browse Applications
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 2 }} spacing="lg">
          {apps.map((app) => (
            <MantineCard
              key={app.path}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => navigate(app.path)}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <Group>
                    <ThemeIcon size={48} radius="md" variant="light" color={app.color}>
                      <app.icon size={24} />
                    </ThemeIcon>
                    <Box>
                      <MantineText fw={600} size="lg">
                        {app.title}
                      </MantineText>
                      <Badge size="xs" variant="light" color={app.color}>
                        {app.pages.length} pages
                      </Badge>
                    </Box>
                  </Group>
                </Group>
                <MantineText size="sm" c="dimmed">
                  {app.description}
                </MantineText>
                <Group gap="xs">
                  {app.pages.map((page) => (
                    <Badge key={page} size="sm" variant="outline" color="gray">
                      {page}
                    </Badge>
                  ))}
                </Group>
              </Stack>
            </MantineCard>
          ))}
        </SimpleGrid>
      </Box>

      {/* Features */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Title order={4}>What's Included</Title>
          <MantineText size="sm" c="dimmed">
            Each application template features:
          </MantineText>
          <Box component="ul" style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <MantineText size="sm">Multiple interconnected pages with navigation</MantineText>
            </li>
            <li>
              <MantineText size="sm">Realistic mock data that demonstrates component capabilities</MantineText>
            </li>
            <li>
              <MantineText size="sm">Interactive elements - modals, forms, filters, and actions</MantineText>
            </li>
            <li>
              <MantineText size="sm">Responsive layouts optimized for all screen sizes</MantineText>
            </li>
            <li>
              <MantineText size="sm">Theme-aware styling that works with all color schemes</MantineText>
            </li>
          </Box>
        </Stack>
      </MantineCard>
    </Stack>
  );
}
